import { useState, useEffect, useCallback } from "react"

/**
 * Custom hook for PWA installation functionality
 *
 * Provides:
 * - Detection of installability (beforeinstallprompt event)
 * - Detection of standalone mode (already installed)
 * - Platform detection (iOS requires different install flow)
 * - Install prompt trigger function
 *
 * @returns {Object} PWA install state and functions
 */
export const usePWAInstall = () => {
	const [deferredPrompt, setDeferredPrompt] = useState(null)
	const [isInstallable, setIsInstallable] = useState(false)
	const [isInstalled, setIsInstalled] = useState(false)
	const [isIOS, setIsIOS] = useState(false)
	const [isAndroid, setIsAndroid] = useState(false)
	const [isFireTablet, setIsFireTablet] = useState(false)

	useEffect(() => {
		// Check if already installed (standalone mode)
		const checkStandalone = () => {
			const isStandalone =
				window.matchMedia("(display-mode: standalone)").matches ||
				window.navigator.standalone === true ||
				document.referrer.includes("android-app://")
			setIsInstalled(isStandalone)
		}

		checkStandalone()

		// Listen for display mode changes
		const mediaQuery = window.matchMedia("(display-mode: standalone)")
		const handleChange = e => {
			setIsInstalled(e.matches)
		}
		mediaQuery.addEventListener("change", handleChange)

		// Detect platform
		const userAgent = navigator.userAgent || navigator.vendor || window.opera

		// iOS detection (iPad, iPhone, iPod)
		const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream
		// Also check for iPad on iOS 13+ (reports as Mac)
		const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1
		setIsIOS(iOS || iPadOS)

		// Android detection
		const android = /android/i.test(userAgent)
		setIsAndroid(android)

		// Fire Tablet detection (Silk browser)
		const silk = /Silk/i.test(userAgent)
		setIsFireTablet(silk)

		// Listen for install prompt (Chrome/Edge/Android/Fire)
		const handleBeforeInstall = e => {
			// Prevent the mini-infobar from appearing on mobile
			e.preventDefault()
			// Store the event for later use
			setDeferredPrompt(e)
			setIsInstallable(true)
		}

		window.addEventListener("beforeinstallprompt", handleBeforeInstall)

		// Listen for successful installation
		const handleAppInstalled = () => {
			setIsInstalled(true)
			setIsInstallable(false)
			setDeferredPrompt(null)
			// Clear any stored dismissal
			localStorage.removeItem("pwa-banner-dismissed")
		}

		window.addEventListener("appinstalled", handleAppInstalled)

		return () => {
			window.removeEventListener("beforeinstallprompt", handleBeforeInstall)
			window.removeEventListener("appinstalled", handleAppInstalled)
			mediaQuery.removeEventListener("change", handleChange)
		}
	}, [])

	/**
	 * Trigger the native install prompt
	 * Only works on platforms that support beforeinstallprompt
	 *
	 * @returns {Promise<boolean>} True if user accepted, false otherwise
	 */
	const promptInstall = useCallback(async () => {
		if (!deferredPrompt) {
			console.warn("Install prompt not available")
			return false
		}

		try {
			// Show the install prompt
			deferredPrompt.prompt()

			// Wait for user response
			const { outcome } = await deferredPrompt.userChoice

			// Clear the deferred prompt - it can only be used once
			setDeferredPrompt(null)

			if (outcome === "accepted") {
				setIsInstallable(false)
				return true
			}

			return false
		} catch (error) {
			console.error("Error prompting install:", error)
			return false
		}
	}, [deferredPrompt])

	/**
	 * Check if the banner was recently dismissed
	 *
	 * @param {number} daysToHide - Number of days to respect dismissal
	 * @returns {boolean} True if banner should be hidden
	 */
	const wasDismissedRecently = useCallback((daysToHide = 7) => {
		const dismissed = localStorage.getItem("pwa-banner-dismissed")
		if (!dismissed) return false

		const dismissedDate = new Date(dismissed)
		const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)

		return daysSinceDismissed < daysToHide
	}, [])

	/**
	 * Dismiss the install banner for a period of time
	 */
	const dismissBanner = useCallback(() => {
		localStorage.setItem("pwa-banner-dismissed", new Date().toISOString())
	}, [])

	/**
	 * Get platform-specific install instructions
	 *
	 * @returns {Object} Instructions object with steps
	 */
	const getInstallInstructions = useCallback(() => {
		if (isIOS) {
			return {
				platform: "iOS",
				browser: "Safari",
				steps: [
					"Tap the Share button (square with arrow pointing up)",
					'Scroll down and tap "Add to Home Screen"',
					'Tap "Add" in the top right corner',
					"Find the Guerrilla Ed icon on your home screen!",
				],
			}
		}

		if (isFireTablet) {
			return {
				platform: "Fire Tablet",
				browser: "Silk",
				steps: [
					"Tap the menu button (three dots)",
					'Tap "Add to Home screen" or "Install app"',
					'Tap "Add" to confirm',
					"Find the Guerrilla Ed icon on your home screen!",
				],
			}
		}

		if (isAndroid) {
			return {
				platform: "Android",
				browser: "Chrome",
				steps: [
					"Tap the menu button (three dots)",
					'Tap "Add to Home screen" or "Install app"',
					'Tap "Add" or "Install"',
					"Find the Guerrilla Ed icon on your home screen!",
				],
			}
		}

		// Desktop (Chrome/Edge)
		return {
			platform: "Desktop",
			browser: "Chrome or Edge",
			steps: [
				"Look for the install icon in the address bar (computer with arrow)",
				'Click "Install"',
				"The app will open in its own window",
			],
		}
	}, [isIOS, isAndroid, isFireTablet])

	return {
		// State
		isInstallable,
		isInstalled,
		isIOS,
		isAndroid,
		isFireTablet,
		hasPrompt: !!deferredPrompt,

		// Actions
		promptInstall,
		dismissBanner,
		wasDismissedRecently,
		getInstallInstructions,
	}
}

export default usePWAInstall
